
import { useEffect, useState, lazy, Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import Header from "@/components/layout/Header";
import Sidebar from "@/components/layout/Sidebar";
const CreateListDialog = lazy(() => import("@/components/ui/CreateListDialog"));
import PrimaryListSection from "@/components/home/PrimaryListSection";
import MostPurchasedSection from "@/components/home/MostPurchasedSection";
import UpcomingProductsSection from "@/components/home/UpcomingProductsSection";
import { useInitialSync } from "@/hooks/useInitialSync";
import { SyncStatusIndicator } from "@/components/ui/SyncStatusIndicator";
import TutorialSpotlight from "@/components/tutorial/TutorialSpotlight";
import { useTutorialManager } from "@/hooks/useTutorialManager";
import { useUpcomingProducts } from "@/hooks/useUpcomingProducts";
import { useOfflineProducts } from "@/hooks/useOfflineProducts";
import { safeToastError, safeToastSuccess } from "@/components/ui/toastManager";
import { queryClient } from "@/services/queryClient";
import { getEnhancedPrimaryList } from "@/services/shopping/lists/primary-list-enhanced.service";
import { addRecurringProductToList } from "@/services/recurring-products-to-list.service";
import { createEntityWithOfflineFallback } from "@/services/createEntityWithOfflineFallback";
import logger from "@/utils/logger";
import { homePageTutorial } from "@/components/tutorial/HomePageTutorial";
import { primaryListOfflineService } from "@/services/offline/primary-list-offline.service";
import HelpButton from "@/components/tutorial/HelpButton";
import { useAuth } from "@/context/auth/AuthContext";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { useOfflineShoppingLists } from "@/hooks/useOfflineShoppingLists";
import { ShoppingList } from "@/types";
import { useSmartAutoSync } from '@/hooks/useSmartAutoSync';
import { smartAutoSyncService } from '@/services/SmartAutoSyncService';
import { useResetModals } from '@/hooks/useResetModals';
import { useShoppingItemsSubscription } from '@/hooks/useShoppingItemsSubscription';

const HomePage = () => {
  const { user, logout } = useAuth();
  useResetModals();
  // La home mostra solo la lista primaria (PrimaryListSection): filtra il
  // realtime su quella. La query è già in cache (stessa key di PrimaryListSection),
  // quindi non genera un fetch aggiuntivo.
  const { data: primaryList } = useQuery({
    queryKey: ['enhanced-primary-list'],
    queryFn: getEnhancedPrimaryList
  });
  useShoppingItemsSubscription(primaryList?.id);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCreatingList, setIsCreatingList] = useState(false);
  const { theme } = useThemeConfig();

  const { isSyncing, syncStatus } = useInitialSync();
  const { products, isLoading: isProductsLoading, createProduct } = useOfflineProducts();
  const { products: upcomingProducts, isLoading: isUpcomingLoading } = useUpcomingProducts();
  const { shoppingLists } = useOfflineShoppingLists();

  useSmartAutoSync({
    entities: ['shoppingLists', 'shoppingItems', 'recurringProducts'],
    enabled: true
  });

  useEffect(() => {
    smartAutoSyncService.forceSync('page-entry', [
      'shoppingLists',
      'shoppingItems',
      'recurringProducts'
    ]);
  }, []);

  useEffect(() => {
    const unsubscribe = smartAutoSyncService.addListener(status => {
      if (status.includes('sync-started')) {
        logger.log('HomePage', '🔄 Sync started');
      }

      if (status.includes('sync-completed')) {
        logger.log('HomePage', '✅ Sync completed');
      }
    });

    return unsubscribe;
  }, []);



  // ✅ OTTIMIZZATO: Logging ridotto
  useEffect(() => {
    if (syncStatus?.timestamp && products.length > 0) {
      logger.log('HomePage', '🏠 Data loaded successfully', {
        productsCount: products.length,
        syncComplete: syncStatus.isComplete
      });
    }
  }, [syncStatus?.isComplete, products.length]);

  const { isOpen: isTutorialOpen, openTutorial, closeTutorial } = useTutorialManager({
    pageId: 'home-page',
    showOnFirstVisit: true
  });

  const handleListCreated = () => {
    setIsCreateDialogOpen(false);
  };

  const handleCreateList = async (name: string): Promise<void> => {
    setIsCreatingList(true);
    const result = await createEntityWithOfflineFallback<ShoppingList>({
      entityKey: 'shoppingLists',
      data: { name },
      supabaseCreateFn: async list => {
        const { createShoppingList } = await import('@/services/shopping/lists/create-list.service');
        await createShoppingList(list.name);
      },
      toastSuccessMessage: 'Lista creata con successo',
      toastErrorMessage: 'Impossibile creare la lista',
      onAfterCreate: () => {
        queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
        setIsCreatingList(false);
        setIsCreateDialogOpen(false);
      }
    });

    if (result.success) {
      safeToastSuccess(result.message);
    } else {
      safeToastError(result.message);
    }
  };

  const handleAddToList = async (productId: string) => {
    try {
      const primaryList = await getEnhancedPrimaryList();

      if (!primaryList) {
        safeToastError('Lista principale non trovata');
        return;
      }

      const success = await addRecurringProductToList(primaryList.id, productId);

      if (success) {
        safeToastSuccess('Prodotto aggiunto alla lista della spesa');
      } else {
        safeToastError('Impossibile aggiungere il prodotto alla lista');
      }

      await queryClient.invalidateQueries({ queryKey: ['enhanced-primary-list'] });
      await queryClient.invalidateQueries({ queryKey: ['shopping-items', primaryList.id] });
    } catch (error) {
      console.error('Error adding to list:', error);
      safeToastError("Errore durante l'aggiunta alla lista");
    }
  };

  const handleHelpClick = () => {
    openTutorial();
  };

  return (
    <div
      className="min-viewport-height"
      style={{
        backgroundColor: theme.backgroundColor || '#f5f5f5',
        color: theme.textColor || '#000000'
      }}
    >
      <Header 
        onMenuClick={() => setSidebarOpen(true)}
        onAddClick={() => setIsCreateDialogOpen(true)}
        onHelpClick={handleHelpClick}
      />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="container mx-auto px-4 py-2 space-y-5">
        {/* {isSyncing && (
          <div
            className="mb-4 p-3 rounded-lg border"
            style={{
              backgroundColor: `${theme.primaryColor}20`,
              borderColor: theme.primaryColor,
            }}
          >
            <SyncStatusIndicator showDetails className="justify-center" />
          </div>
        )} */}

        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold" style={{ color: theme.sectionTitleColor }}>
              Benvenuto {user.name}
            </h1>
          </div>
          <HelpButton onClick={openTutorial} />
        </div>

        <div data-tutorial="primary-list-section" className="primary-list-section">
          <PrimaryListSection />
        </div>
        <div data-tutorial="most-purchased">
          <MostPurchasedSection />
        </div>
        <div className="upcoming-products-section" data-tutorial="upcoming-products">
          <UpcomingProductsSection
            upcomingProducts={upcomingProducts}
            isLoading={isUpcomingLoading}
            onAddToList={handleAddToList}
          />
        </div>

        <Suspense fallback={null}>
          <CreateListDialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
            listCount={shoppingLists?.length || 0}
            onCreateList={handleCreateList}
            isCreating={isCreatingList}
          />
        </Suspense>

        {isTutorialOpen && homePageTutorial.length > 0 && (
          <TutorialSpotlight
            steps={homePageTutorial}
            isOpen={isTutorialOpen}
            onClose={closeTutorial}
            pageId="home-page"
            showProgressBar={true}
          />
        )}
        <div className="h-10" />
      </main>
    </div>
  );
};

export default HomePage;
